import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { ApiAppUser } from '../../core/api/api.types';
import { ReferenceApiService } from '../../core/api/reference-api.service';
import { UsersApiService } from '../../core/api/users-api.service';

@Component({
  selector: 'op-customers-page',
  imports: [
    FormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './customers.page.html',
  styleUrl: './customers.page.scss',
})
export class CustomersPage implements OnInit {
  private readonly usersApi = inject(UsersApiService);
  private readonly referenceApi = inject(ReferenceApiService);

  readonly allUsers = signal<ApiAppUser[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  readonly cityOptions = signal<Array<{ code: string; label: string }>>([]);

  // ── Filter state ──────────────────────────────────────────────────────────
  readonly searchQuery = signal('');
  readonly emailQuery = signal('');
  readonly cityFilter = signal<string[]>([]);
  cityDropdownOpen = false;

  // ── Filtered list ─────────────────────────────────────────────────────────
  readonly users = computed<ApiAppUser[]>(() => {
    const q = this.searchQuery().trim().toLowerCase();
    const eq = this.emailQuery().trim().toLowerCase();
    const cities = this.cityFilter();
    let result = this.allUsers();

    if (q) {
      result = result.filter(u =>
        u.id.toLowerCase().includes(q) ||
        (u.preferred_name && u.preferred_name.toLowerCase().includes(q)),
      );
    }

    if (eq) {
      result = result.filter(u => u.email?.toLowerCase().includes(eq));
    }

    if (cities.length) {
      result = result.filter(u => cities.includes(u.city_code));
    }

    return result;
  });

  async ngOnInit(): Promise<void> {
    try {
      const [usersRes, citiesRes] = await Promise.all([
        this.usersApi.listAppUsers(),
        this.referenceApi.cities(),
      ]);
      this.allUsers.set(usersRes.items);
      this.cityOptions.set(
        citiesRes.items.map(c => ({ code: c.code, label: c.label ?? c.code })),
      );
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to load customers');
    } finally {
      this.loading.set(false);
    }
  }

  isCitySelected(code: string): boolean {
    return this.cityFilter().includes(code);
  }

  toggleCity(code: string): void {
    const current = this.cityFilter();
    this.cityFilter.set(
      current.includes(code) ? current.filter(c => c !== code) : [...current, code],
    );
  }

  formatDate(iso: string): string {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-CA', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch {
      return '—';
    }
  }
}